// GLOBAL constnats
import _startCase from 'lodash/startCase';
import moment from 'moment-timezone';

/**
 * Dynamically determine the API endpoint based on the current hostname.
 * This allows a single deployment to serve multiple domains with different API endpoints.
 */
const getAPIRoot = (): string => {
  const hostname = window.location.hostname;

  // Indo-specific routing
  if (hostname.includes('indo.perxtech.io')) {
    return 'https://api.indo.perxtech.io';
  }
  if (hostname.includes('indo.perxtech.net')) {
    return 'https://api.indo.perxtech.net';
  }

  // BTPN-specific routing
  if (hostname.includes('btpn.perxtech.io')) {
    return 'https://api.btpn.perxtech.io';
  }
  if (hostname.includes('btpn.perxtech.net')) {
    return 'https://api.btpn.perxtech.net';
  }

  // Fallback to environment variable or default
  //@ts-ignore
  return window.__API_URL__ || 'https://api.perxtech.net';
};

export const API_ROOT = getAPIRoot();
//@ts-ignore
export const IS_PF = window.__IS_PF__;
export const METABASE_API_ROOT = 'https://metabase-api.perxtech.io';

export const PERX_MAIL_DOMAIN = '@perxtech.com';

export const DATE_FORMAT = 'DD MMM YYYY';
export const TIME_FORMAT = 'HH:mm';
export const TIMEZONE_FORMAT = '(Z)';
export const DB_RECORD_DATETIME_FORMAT = 'DD MMM YYYY HH:mm (Z)';

export const DEFAULT_PAGE_SIZE = 25;
export const MID_PAGE_SIZE = 10;
export const SMALL_PAGE_SIZE = 5;

export const MAX_INTEGER_VALUE = 2147483647; // 4 bit signed integer max

export const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
// TODO: extend the env config in the build process instead of doing this
export const IS_STAGING =
  IS_DEVELOPMENT ||
  ((window.location.hostname === 'dashboard.perxtech.io' ||
    window.location.hostname.startsWith('feature')) &&
    process.env.NODE_ENV === 'production');
export const IS_PRODUCTION = !IS_STAGING && process.env.NODE_ENV === 'production';
export const HIDE_ON_PRODUCTION = !IS_PRODUCTION;

export const POINTS_BURNED = 'Points Burned';
type MomentFormat =
  | 'dddd' // Full day name, e.g., Sunday
  | 'ddd'; // Short day name, e.g., Sun

export const DAY_KEY_VALUE = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  0: 'Sunday'
};

export const getDaysOfWeekOptions = (
  labelFormat: MomentFormat = 'dddd',
  valueFormat: MomentFormat = 'ddd',
  isValueLowerCase: boolean = true
): { label: string; value: string }[] => {
  return Object.entries(DAY_KEY_VALUE).map(([i, _]) => {
    const day = moment().startOf('week').add(i, 'days');
    return {
      label: day.format(labelFormat),
      value: isValueLowerCase ? day.format(valueFormat).toLowerCase() : day.format(valueFormat)
    };
  });
};

export const DAYS = Object.entries(DAY_KEY_VALUE).map(([id, name]) => ({ id: +id, name }));

export const TIMEZONES = moment.tz
  .names()
  .map(_ => ({
    value: _,
    offsetLabel: moment.tz(moment(), _).format('Z'),
    label: _startCase(_),
    offset: moment.tz(moment(), _).utcOffset()
  }))
  .sort((a, b) => a.offset - b.offset);

export const MONTHS = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December'
};

export const TENANTS = {
  STARHUB: 'starhub',
  MERCK: 'merck',
  ABENSON: 'abenson',
  UOB: 'uob_uob',
  COMMBANK: 'commbank',
  ZEAL: 'zeal',
  RAZER: 'razer',
  ZAW: 'prod_zaw',
  DAIICHI: 'dlvn',
  HSBC: 'hsbc_hsbc',
  HSBCVN: 'hsbcvn',
  GLOBEATHOME: 'globeathome',
  GLONEONE: 'globeone',
  HOOLAH: 'hoolah',
  HOOLAH_HK: 'hoolah_hk',
  HOOLAH_MY: 'hoolah_my',
  FSI: 'fsi',
  TELCO: 'telco',
  RETAIL: 'retail',
  INSURANCE: 'insurance',
  FINTECH: 'fintech',
  RUSH: 'rush',
  CWC: 'cwc',
  SIAM_PIWAT: 'siampiwat',
  SIAM_PIWAT_PREPROD: 'spw_preprod',
  TRANSCYCLE: 'transcycle',
  TRANSCYCLE_PREPROD: 'transcycle_preprod',
  CS: 'cs',
  GENERIC: 'generic',
  AFFIRM: 'affirm',
  KISSHT: 'kissht'
};

export const THEMED_TENANTS = [TENANTS.ZEAL, TENANTS.RUSH, TENANTS.CWC, TENANTS.AFFIRM];

export const TENANT_DISPLAY_NAME = {
  [TENANTS.STARHUB]: 'Starhub',
  [TENANTS.MERCK]: 'Merck',
  [TENANTS.ABENSON]: 'Abenson',
  [TENANTS.UOB]: 'UOB',
  [TENANTS.ZEAL]: IS_PRODUCTION ? 'Zeal' : 'POSable',
  [TENANTS.RAZER]: 'Razer',
  [TENANTS.DAIICHI]: 'Daiichi',
  [TENANTS.HSBCVN]: 'HSBC VN',
  [TENANTS.HSBC]: 'HSBC PH',
  [TENANTS.GLOBEATHOME]: 'Globe At Home',
  [TENANTS.GLONEONE]: 'Globe One',
  [TENANTS.HOOLAH]: 'HOOLAH SG',
  [TENANTS.HOOLAH_HK]: 'HOOLAH HK',
  [TENANTS.HOOLAH_MY]: 'HOOLAH MY',
  [TENANTS.COMMBANK]: 'CommBank',
  [TENANTS.SIAM_PIWAT]: 'Siam Piwat',
  [TENANTS.AFFIRM]: 'Affirm'
};

export const LIVE_TENANTS = [
  TENANTS.ABENSON,
  TENANTS.STARHUB,
  TENANTS.UOB,
  TENANTS.MERCK,
  TENANTS.ZEAL,
  TENANTS.RAZER,
  TENANTS.ZAW,
  TENANTS.GLOBEATHOME,
  TENANTS.GLONEONE,
  TENANTS.HSBC,
  TENANTS.HSBCVN,
  TENANTS.HOOLAH,
  TENANTS.HOOLAH_HK,
  TENANTS.HOOLAH_MY,
  TENANTS.COMMBANK
];

// notifications
export const DURATION_TIMER = 2;

// Errors
export const ERRORS = {
  REQUIRED: 'This field is required.',
  END_TIME_ERROR: 'End date cannot be before start date.',
  NUMBER_SIZE_MORE_ZERO: 'Value must be more then zero.',
  NUMBER_SIZE_MORE_OR_ZERO: 'Value must be greater than or equal to zero.',
  WRONG_TYPE: 'This field is wrong',
  APPROVE_OWN_REQUEST: 'You cannot approve your own request.'
};

// Translations
export const fieldsWithTranslations = [
  'name',
  'subtitle',
  'description',
  'terms_and_conditions',
  'steps_to_redeem'
];

export interface Feature {
  key: string;
  label: string;
  category?: string;
  subtext?: string;
  disabled?: boolean;
}

const FEATURE_SUBTEXT = {
  QA: 'Unsupported. Only accepting feedback from QA. Personally ensure what is shown is working before demo.',
  IN_DEVELOPMENT:
    'Unsupported. Not accepting feedback from anyone. Expect this to not work, enable at own risk.',
  DEPRECATED: 'Deprecated legacy feature pending review for removal.'
};

type FeatureMap = Record<string, Feature>;

/**
 * helper function which infers keys and restricts values to Feature type
 * relies on the tenant config features
 * source: https://stackoverflow.com/a/54598743/5280598
 */
const asFeatureFlagTypes = <T extends FeatureMap>(et: { [K in keyof T]: T[K] }) => et;

/**
 * Let's keep items in this dictionary sorted by key.
 *
 * It will help us to
 * - quicker find a desired item
 * - make more consistent naming for new items
 * - prevent merge conflicts when there are several pull requests
 * with new feature flags at the end of the list
 */
export const FEATURES = asFeatureFlagTypes({
  ACCORDION_DESCRIPTION_TEXT_EDITOR_IN_REWARDS: {
    key: 'accordion_description_text_editor_in_rewards',
    label: 'Accordion description text editor in reward',
    subtext: FEATURE_SUBTEXT.QA
  },
  ACHIEVED_NEXT_TIER: {
    key: 'achieved_next_tier',
    label: 'Enabled Next Tier Rule Trigger',
    subtext:
      'Allows Enabled Next Tier as a rule trigger in the Rule builder. Not scalable, do not enable for tenants with more than 250k users or 10k loyalty earn transactions per day'
  },
  AI_ASSISTANT: {
    key: 'ai_assistant',
    label: 'Orbit AI Insight Assistant',
    subtext:
      'Enables the Perx Orbit AI assistant sidebar. Requires the Perx AI WorkSpace flag; Intelligence Suite context also needs the Intelligence Suite flag.'
  },
  ORBIT_LAYER_4: {
    key: 'orbit_layer_4',
    label: 'Orbit Layer 4 Actions',
    subtext:
      'Lets Orbit propose and run configuration actions (v1: deactivate a transaction rule) from the assistant. Requires the Orbit AI Insight Assistant flag.'
  },
  ALPHABET_VOUCHER_CODE: {
    key: 'alphabet_voucher_code_format',
    label: 'Voucher Code Format - Alphabet',
    subtext: 'Enables alphabet voucher code format option.'
  },
  ALPHANUMERIC_VOUCHER_CODE: {
    key: 'alphanumeric_voucher_code_format',
    label: 'Voucher Code Format - Alphanumeric',
    subtext: 'Enables alphanumeric voucher code format option.'
  },
  ALTERNATE_WYSIWYG: {
    key: 'alt_wysiwyg',
    label: 'Alternate Rich Text editor',
    subtext: 'Switches between different Rich Text editors in forms.'
  },
  AGENT_CONTROL_PANEL: {
    key: 'agent_control_panel',
    label: 'Agent Control Panel',
    subtext: 'Enables the Perx AI Agent Control Panel module.'
  },
  AUDIENCE: {
    key: 'audience',
    label: 'Audience',
    subtext: 'Enables the Audience module. ' + FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  AUDIENCE_DEMOGRAPHICS_SELECTION: {
    key: 'audience_demographics_selection',
    label: 'Audience Demographics Selection',
    subtext: 'Enables audience demographics filter selection. Requires custom Pers backend setup'
  },
  AUDIENCE_EMAIL_TAGS: {
    key: 'audience_email_tags',
    label: 'Audience Email Tags',
    subtext: 'Enables audience email tags filter selection. Requires email tags setup from customer'
  },
  AUDIT_LOG: {
    key: 'audit_log',
    label: 'Audit Log',
    subtext: 'Enables the display of Audit logs in applicable pages'
  },
  // AUTO_ENROL_USERS: {
  //   key: 'auto_enrol_users',
  //   label: 'Auto Enrol Users',
  //   subtext: 'Enables auto enrollment feature in campaign module.'
  // },
  AUTO_FILL_STAMPS: {
    key: 'auto_fill_stamps',
    label: 'Auto Fill Stamp',
    subtext: 'Enables auto enrollment feature in stamp campaign module.'
  },
  AUTO_REDEEM_INSTANT_OUTCOME_TRANSACTION: {
    key: 'auto_redeem_instant_outcome_transaction',
    label: 'Auto Redeem Instant Outcome Transaction',
    subtext:
      'Enables auto redeem instant outcome feature for instant reward to be issued just like any other stamp/progress campaign.'
  },
  AUTO_SEND_PUSH_NOTIFICATION_ON_INSTANT_OUTCOME_TRANSACTION_ISSUED: {
    key: 'auto_send_push_notification_on_instant_outcome_transaction_issued',
    label: 'Enable Push Notification for Instant Reward Campaign Triggers',
    subtext:
      'Enables auto send push notification on instant outcome transaction issued for instant reward to be issued just like any other stamp/progress campaign.'
  },
  MICROSITE_NOTIFICATION_AUTO_REDEEM_OUTCOME: {
    key: 'microsite_notification_auto_redeem_outcome',
    label: 'Enable Microsite Notification for Instant Reward Campaign Auto Redeem Triggers',
    subtext:
      'Enables microsite notifications on auto redeemed instant outcome for instant reward campaign through notification API like stamp/quest/progress campaign'
  },
  AUDIENCE_COUNT_SIZE: {
    key: 'audience_count_size',
    label: 'Audience - Show Audience Count Size',
    subtext:
      'Allows the user to click on the audience and calculate the realtime size of that particular audience.'
  },
  BADGES: {
    key: 'badges',
    label: 'Badges',
    subtext: 'Enables Badges module where you can create badges to be used as outcomes.'
  },
  BRANDS: {
    key: 'brands',
    label: 'Brands',
    subtext: FEATURE_SUBTEXT.DEPRECATED
  },
  BUBBLE_POP: {
    key: 'bubble_pop',
    label: 'Game Type - Bubble Pop',
    subtext: 'Enables Game type - Bubble Pop'
  },
  BULK_ACTIONS: {
    key: 'bulk_actions',
    label: 'Bulk Actions',
    subtext: 'Currently does not control anything'
  },
  BULK_ACTIONS_LOG: {
    key: 'bulk_actions_log',
    label: 'Bulk Actions - Log Files',
    subtext: 'Enables downloading log files for bulk uploads'
  },
  BULK_ACTIONS_PROCESSED_ROWS: {
    key: 'bulk_actions_processed_rows',
    label: 'Bulk Actions - Processed Rows',
    subtext: 'Shows processed rows column in bulk actions list table.'
  },
  BULK_ACTIONS_UPLOAD_USERS: {
    key: 'bulk_actions_upload_users',
    label: 'Bulk Actions Upload Users',
    subtext: 'Enables bulk actions upload users'
  },
  BULK_ACTIONS_ADD_MARCHANT_NAME: {
    key: 'add_merchant_name_in_bulk_upload_points',
    label: 'Add Merchant Name In Bulk Upload Points',
    subtext: 'Enables Add Merchant name for Bulk Upload'
  },
  BULK_ISSUE_RAFFLE_TICKETS: {
    key: 'bulk_issue_raffle_tickets',
    label: 'Issue Raffle Tickets via Bulk Action',
    subtext:
      'Allow dashboard users to issue multiple raffle tickets to multiple customers through Bulk Action by uploading a CSV file.'
  },
  BULK_UPDATE_VOUCHERS_EXPIRY: {
    key: 'bulk_update_voucher_expiry',
    label: 'Bulk Actions - Update Vouchers Expiry Date'
  },
  BUSINESS_INTELLIGENCE: {
    key: 'business_intelligence',
    label: 'Business Intelligence',
    subtext: 'Enables Analytics module'
  },
  CAMPAIGN_DUPLICATION: {
    key: 'campaign_duplication',
    label: 'Campaign Duplication',
    subtext:
      'Enables action to duplicate campaign from the campaign list page. ' + FEATURE_SUBTEXT.QA
  },
  DYNAMIC_EXPIRY_FOR_POINTS_AS_AN_OUTCOME: {
    key: 'dynamic_expiry_for_points_as_an_outcome',
    label: 'Campaign Outcome - Dynamic Points Expiry Date',
    subtext: 'Enables the feature to to set per campaign points expiry.'
  },
  CAMPAIGNS: {
    key: 'campaigns',
    label: 'BI - Campaigns',
    subtext: 'Displays Campaigns tab in Analytics module'
  },
  CASHBACK_MODULE: {
    key: 'cashback',
    label: 'Cashback Module',
    subtext: 'Enables the Cashback Module'
  },
  CATALOGS: {
    key: 'catalogs',
    label: 'Catalogues',
    subtext: 'Enables the Catalogues module and all related sections in other modules'
  },
  CATALOGUE_AUDIENCE: {
    key: 'catalogue_audience',
    label: 'Catalogue Audience',
    subtext: 'Enables setting Audience filters during Catalogue creation/editing'
  },
  CATALOGUE_ITEMS: {
    key: 'catalogue_items',
    label: 'Catalogue Items',
    subtext: 'Enables catalogue item card in catalogue show page.'
  },
  CATALOGUE_TAG_MERCHANT: {
    key: 'catalogue_tag_merchant',
    label: 'Catalogue Tag Merchant'
  },
  CATALOGUE_TERMS_CONDITIONS: {
    key: 'catalogue_terms_conditions',
    label: 'Catalogue Terms and Conditions',
    subtext: 'Enables Terms and Conditions for Catalogue creation/editing'
  },
  CHOOSE_YOUR_REWARD: {
    key: 'choose_campaign_outcome',
    label: 'Choose Your Reward',
    subtext: 'Enables the Choose Your Reward feature in for campaign outcomes.'
  },
  CLAW_MACHINE: {
    key: 'claw_machine',
    label: 'Game Type - Claw Machine',
    subtext: 'Enables Game type - Claw Machine'
  },
  CREATE_POINTS: {
    key: 'create_points',
    label: 'Bulk Upload - Create Points',
    subtext: FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  CTA_BUTTON_EXTERNAL_LINK: {
    key: 'cta_button_external_link',
    label: 'Campaign CTA Button External Link',
    subtext: 'Enables campaign CTA button external link text input'
  },
  CUSTOM_APIS: {
    key: 'custom_apis',
    label: 'Custom APIS',
    subtext: 'Enables Custom API settings in Settings -> General'
  },
  DATA_RETENTION_POLICY: {
    key: 'customer_anonymization_policy',
    label: 'Data Retention Policy'
  },
  CUSTOM_FIELDS: {
    key: 'custom_fields',
    label: 'Custom Fields',
    subtext:
      'Custom Fields is an enterprise escape hatch for when a tenant want to pass data on the API.'
  },
  CURRENCY_CONVERSION_RATE: {
    key: 'currency_conversion_rate',
    label: 'Currency Conversion Rate',
    subtext: FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  CUSTOM_FIELDS_TEXTAREA: {
    key: 'custom_fields_textarea',
    label: 'Custom Fields Teaxtarea',
    subtext: 'Allows setting multi-line values for custom fields.'
  },
  CUSTOMER_BULK_UPLOAD_TRIGGER_POST_CREATE_ACTIONS: {
    key: 'customer_bulk_upload_trigger_post_create_actions',
    label: 'Customer Bulk Upload Trigger Create Actions',
    subtext:
      'Enrolls users to loyalty programs and creates sing up event when user uploaded via v4/dash/file_imports enpoint'
  },
  CUSTOMER_DO_NOT_CAPITALISE_PERSONAL_PROPERTIES: {
    key: 'customer_do_not_capitalise_personal_properties',
    label: 'Customer Module - Do not Capitalise Personal Properties',
    subtext: 'Prevents automatic capitalization of personal property names in the Customer module.'
  },
  CUSTOMER_ENROL_UNENROL_CAMPAIGNS: {
    key: 'customer_enrol_unenrol_campaigns',
    label: 'Customer - Enrol/Un-enrol in Campaigns'
  },
  USER_ROLE_GRANULAR_PERMISSIONS_CUSTOMER_MODULE: {
    key: 'granular_permissions_for_customer_module',
    label: 'User Role Granular Permissions - Customer Module',
    subtext:
      'Enables granular permission settings for Customer Module. Cannot be used with Maker-Checker for Customer Module'
  },
  // CUSTOMERS: { key: 'customer_management' },
  CUSTOMERS_INITIAL_TAB: {
    key: 'customers_initial_tab',
    label: 'Customers Initial Tab'
  },
  DISABLE_SCHEDULE_CAMPAIGN_BUTTON: {
    key: 'disable_schedule_campaign_button',
    label: 'Disable Schedule Campaign Button',
    subtext: 'Hides the Schedule Campaign button in the Edit Campaign screen'
  },
  EDITABLE_INDUSTRY: {
    key: 'editable_industry',
    label: 'Editable Industry',
    subtext:
      'Allows a Tenant Admin to change the Industry in Localization Settings after it has already been set. Without this, Industry is a one-time setting.'
  },
  ENABLE_CAMPAIGN_ENROLMENT_NOTIFICATION: {
    key: 'enable_campaign_enrolment_notification',
    label: 'Enable Campaign Enrolment Notification',
    subtext: 'Enables webhook notifications for enrolling in a specific campaign'
  },
  ENROLL_ELIGIBLE_USERS_TO_CAMPAIGNS: {
    key: 'enroll_eligible_users_to_campaigns',
    label: 'Allow Auto Enrol with Campaign Duration Mechanics Enabled',
    subtext:
      'Allows Campaigns with Auto Enrol ON and Campaign Duration Mechanics ON. Can cause performance problems. Do not Enable except for BTPN without Product & Engineering approval'
  },
  ENROLLABLE_UNTIL: {
    key: 'enrollable_until',
    label: 'Enrollable until',
    subtext: 'Enables setting of enrolling period end date'
  },
  EARLY_DISPLAY_CAMPAIGNS: {
    key: 'early_display_campaigns',
    label: 'Early Display Campaigns',
    subtext: 'Enables campaigns to be displayed before the start date'
  },
  EXPERIENCE_STUDIO: {
    key: 'experience_studio',
    label: 'Experience Studio',
    subtext:
      'Enables Experience Studio (Branding, Microsite landing page builder and Control settings) on the Microsite page. Requires the Perx AI WorkSpace and Microsite Configuration flags.'
  },
  GAME_CAMPAIGN: {
    key: 'game_campaign',
    label: 'Game Campaigns',
    subtext: 'Enables game campaigns'
  },
  GAME_TRY: {
    key: 'game_tries_as_campaign_outcome',
    label: 'Game Try as an outcome',
    subtext: 'Enables Game try for campaign as an outcome'
  },
  GAMES_VARIABLE_POINT: {
    key: 'games_variable_point',
    label: 'Variable Point as an outcome for games',
    subtext: FEATURE_SUBTEXT.QA
  },
  GENERATE_PF_DAILY_TRANSACTION_REPORT: {
    key: 'generate_pf_daily_transaction_report',
    label: 'Daily Transaction Report (SARG)',
    subtext: 'Enable to trigger the “Daily Transaction Report (SARG)"'
  },
  GRANULAR_USER_PERMISSIONS: {
    key: 'granular_user_permissions',
    label: 'Granular User Permissions',
    subtext: 'Granular User Permissions'
  },
  GENERATE_USER_IDENTIFIER: {
    key: 'autogenerate_random_user_identifier',
    label: 'Generate Random User Identifier',
    subtext: 'Enables the feature to autogenerate random user identifier'
  },
  SINGLE_GL_ACCOUNT_LOOKUP_TABLE: {
    key: 'single_gl_account_lookup_table',
    label: 'Single Select gl_account in Rule, Customer, Campaign, Loyalty',
    subtext: 'Single Select gl_account in Rule, Customer, Campaign, Loyalty'
  },
  GL_ACCOUNT_LOOKUP_TABLE: {
    key: 'gl_account_lookup_table',
    label: 'Multiselect gl_account in Rule, Customer, Campaign, Loyalty',
    subtext: 'Multiselect gl_account in Rule, Customer, Campaign, Loyalty'
  },
  QUIZ_VARIABLE_POINT: {
    key: 'quiz_variable_point',
    label: 'Variable Point as an outcome for quiz',
    subtext: FEATURE_SUBTEXT.QA
  },
  GENERATE_AUDIENCE_FROM_TRANSACTION_BREAKDOWN_REPORT: {
    key: 'generate_audience_from_transaction_breakdown_report',
    label: 'Generate audience directly from Reports',
    subtext:
      'Enables audience creation directly from reports module. Currently available only for Transaction Breakdown Report.'
  },
  HEXANUMERIC_VOUCHER_CODE: {
    key: 'hexanumeric_voucher_code_format',
    label: 'Voucher Code Format - Hexanumeric',
    subtext: 'Enables hexanumeric voucher code format option.'
  },
  HIDE_DELETION_REQUESTED_MESSAGE_IN_CUSTOMER_MODULE: {
    key: 'hide_deletion_requested_message_in_customer_module',
    label: 'Hide Deletion Requested Message in Customer Module',
    subtext: 'Hides the message "Deletion Requested" in the Customer Module'
  },
  // SHOW_INSTANT_REWARDS: {
  //   key: 'show_instant_rewards',
  //   label: 'Campaign Type - Instant Rewards',
  //   subtext: FEATURE_SUBTEXT.IN_DEVELOPMENT
  // },
  HIT_THE_PINATA: {
    key: 'hit_the_pinata',
    label: 'Game Type - Hit The Pinata',
    subtext: 'Enables Game type - Hit The Pinata'
  },
  INSTANT_REWARD_CAMPAIGN: {
    key: 'instant_reward_campaign',
    label: 'Campaign Type - Instant Rewards',
    subtext: 'Enables Campaign type - Instant Rewards'
  },
  INTELLIGENCE_SUITE: {
    key: 'intelligence_suite',
    label: 'Intelligence Suite',
    subtext:
      'Enables the Intelligence Suite, including Customer Insights, Customer 360 Intelligence and role-based access to intelligence features. Requires the Perx AI WorkSpace flag.'
  },
  INTELLIGENCE_RESPONSIVE_LAYOUT: {
    key: 'intelligence_responsive_layout',
    label: 'Intelligence Suite - Responsive Layout',
    subtext: `Scales Customer Insights and Customer 360 Intelligence down on narrow content columns, including when the Orbit panel is open. ${FEATURE_SUBTEXT.IN_DEVELOPMENT}`
  },
  INVENTORY: {
    key: 'inventories',
    label: 'Inventory',
    subtext: 'Enables the Inventories module'
  },
  INVITE: {
    key: 'invite_campaign',
    label: 'Campaign Type - Referral',
    subtext: 'Enables Campaign type - Referral'
  },
  LABELS: {
    key: 'labels',
    label: 'Labels',
    subtext: 'Enables the Labels module and all related sections in other modules'
  },
  LANDING_PAGE_FOR_GAMES: {
    key: 'landing_page_for_games',
    label: 'Landing page for game campaigns',
    subtext: FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  LANDING_PAGE_FOR_STAMPS: {
    key: 'landing_page_for_stamps',
    label: 'Landing page for stamp campaigns',
    subtext: FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  REFERRAL_ACTION_MECHANICS: {
    key: 'referral_action_mechanics',
    label: 'Referral Action Mechanics',
    subtext: 'Enable the feature to view action mechanics section on referral campaign.'
  },
  REFERRAL_AGGREGATED_COUNT_BULK_UPLOAD: {
    key: 'referral_aggregated_count_bulk_upload',
    label: 'Referral Aggregated Count Bulk Upload',
    subtext:
      'Enables bulk action to process aggregated successful referral counts for a referral campaign.'
  },
  CAMPAIGN_LEVEL_BI: {
    key: 'campaign_level_bi',
    label: 'Campaign Level BI',
    subtext: 'Enable the feature to view insights on each campaign.'
  },
  LEADERBOARDS: {
    key: 'leaderboards',
    label: 'Leaderboards',
    subtext: 'Enables Leaderboards module.'
  },
  LIVE_BI_DATA: {
    key: 'live_bi_data',
    label: 'Live BI Data',
    subtext: 'Switch the analytics data pipeline to real data from dummy data'
  },
  LOCALIZATION_SETTINGS: {
    key: 'localization_settings',
    label: 'Localization Settings',
    subtext: 'Enables Localization settings in Settings -> General' + FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  LOCATION_BASED_RULE: {
    key: 'location_based_rule',
    label: 'Location Based Rule',
    subtext: 'Enables location based rule in progress campaign'
  },
  LOOKUPS: {
    key: 'lookups',
    label: 'Lookups',
    subtext: 'Enables Lookups in setting.'
  },
  LOOKUP_TABLE_VALUES: {
    key: 'lookup_table_value',
    label: 'Value Lookup table',
    subtext: 'Enables Changing Value for Lookup table.'
  },
  UPDATE_LOOKUPS: {
    key: 'lookup_table_value_update',
    label: 'Update Lookup table value',
    subtext: 'Enables Update Lookup table value.'
  },
  LANGUAGE_SELECTOR: {
    key: 'language_selector',
    label: 'Languages',
    subtext:
      'Enables support for multiple languages throughout the app in German, Italian, French and expanding.'
  },
  LOYALTIES: {
    key: 'loyalties',
    label: 'Loyalties',
    subtext: 'Enables the Loyalties module and all related sections in other modules'
  },
  LOYALTY_AUDIENCE: {
    key: 'loyalty_audience',
    label: 'Loyalty Audience',
    subtext: FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  LOYALTY_EXTEND_MEMBERSHIP: {
    key: 'loyalty_extend_membership',
    label: 'Loyalty Extend Membership'
  },
  LOYALTY_NEGATIVE_POINT_BALANCE: {
    disabled: true,
    key: 'negative_point_balance',
    label: 'Allow Negative Loyalty points balance',
    subtext: 'Do not disable if the customer has Negative Point Balance'
  },
  LOYALTY_POINTS_DECIMALS: {
    key: 'loyalty_points_decimals',
    label: 'Loyalty Points Decimals',
    subtext: FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  LOYALTY_POINTS_LIMIT: {
    key: 'loyalty_points_limit',
    label: 'Loyalty Points Limit Per User',
    subtext: 'Allows to configure points limit per user'
  },
  LOYALTY_POINTS_PEER_TO_PEER_TRANSFER_LIMIT: {
    key: 'loyalty_points_peer_to_peer_transfer_limit',
    label: 'Loyalty Points Peer to Peer Transfer Limit',
    subtext: 'Enables setting for limit on points peer to peer Transfer'
  },
  LOYALTY_POINTS_TRANSFER: {
    key: 'loyalty_points_transfer',
    label: 'Loyalty Points Transfer',
    subtext: 'Enables settings for exchange rate between Loyalty programs'
  },
  LOYALTY_POINTS_TRANSFER_LIMITS: {
    key: 'loyalty_points_transfer_limits',
    label: 'Loyalty Points Transfer Limits',
    subtext: 'Enables limits settings for points transfer flow'
  },
  LOYALTY_REVIEW_CYCLES: {
    key: 'loyalty_review_cycles',
    label: 'Loyalty Review Cycles'
  },
  LOYALTY_RULE_BIRTHDAY_CHECKBOX: {
    key: 'loyalty_rule_birthday_checkbox',
    label: 'Loyalty Rule Birthday Checkbox'
  },
  LOYALTY_RULE_TRANSACTION_PROPERTY: {
    key: 'loyalty_rule_transaction_property',
    label: 'Loyalty Rule Transaction Property'
  },
  LOYALTY_RULE_TRANSACTION_TYPE: {
    key: 'loyalty_rule_transaction_type',
    label: 'Loyalty Rule Transaction Type'
  },
  LOYALTY_UPLOAD_PHOTO: {
    key: 'loyalty_upload_photo',
    label: 'Loyalty Upload Photo'
  },
  MAKER_CHECKER_ADMIN_NOTIFICATIONS: {
    key: 'maker_checker_admin_notifications',
    label: 'Maker Checker Emails for Tenant Admins',
    subtext:
      'Enables Maker Checker email notifications to Tenant Admins. Requires Maker Checker Email Notifications to be Enabled'
  },
  MAKER_CHECKER_APPROVERS_NOTIFICATIONS: {
    key: 'maker_checker_approvers_notifications',
    label: 'Maker Checker Email Notifications',
    subtext:
      'Enables email notifications to Approvers whenever a Maker submits a request for Maker Checker'
  },
  MAKER_CHECKER_CUSTOMER_MANAGEMENT: {
    key: 'maker_checker_customer_management',
    label: 'Maker Checker Customer Management',
    subtext:
      'Maker Checker Customer Management. Enabling this feature flag will disable Customer Management Access Permission feature flag.'
  },
  MAKER_CHECKER_CUSTOMER_BULK_ACTIONS: {
    key: 'maker_checker_bulk_actions',
    label: 'Maker Checker Bulk Actions',
    subtext: 'Maker Checker Bulk Actions'
  },
  MAKER_CHECKER_REWARD: {
    key: 'maker_checker_rewards',
    label: 'Maker Checker Reward',
    subtext: 'Enable Maker Checker for Rewards'
  },
  MAKER_CHECKER_CAMPAIGN: {
    key: 'maker_checker_campaigns',
    label: 'Maker Checker Campaign',
    subtext: 'Enable Maker Checker for Campaigns'
  },
  MAKER_CHECKER_RULE: {
    key: 'maker_checker_rules',
    label: 'Maker Checker Rule',
    subtext: 'Enable Maker Checker for Rules'
  },
  MARKETPLACE_VENDOR_DRAGONPASS: {
    key: 'marketplace_vendor_dragonpass',
    label: 'Marketplace Vendor - DragonPass',
    subtext: 'Enables DragonPass rewards in Rewards Marketplace'
  },
  MEDIA_TAG: {
    key: 'media_tag',
    label: 'Media Tag',
    subtext: 'Enables media tag in rule builder.'
  },
  MERCHANT_PINS_BULK_UPLOAD: {
    key: 'merchant_pins_bulk_upload',
    label: 'Merchant Pins Bulk Upload',
    subtext: 'Enables merchant pins bulk upload.'
  },
  MERCHANT_PORTAL: {
    key: 'merchant_portal',
    label: 'Merchant Portal',
    subtext: 'Changes display of Merchants in list and show page based on Merchant approval status.'
  },
  MERCHANT_USERS: {
    key: 'merchant_users',
    label: 'Merchant Users',
    subtext: 'Enables the creation of Merchant users in the Merchant Show page'
  },
  MERCHANTS: {
    key: 'merchants',
    label: 'Merchants',
    subtext:
      'Makes Merchants permissions controls available in User->Roles and also enables Merchant page in Analytics.'
  },
  MICROSITE: {
    key: 'microsite',
    label: 'Microsite Configuration',
    subtext: FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  MULTILINGUAL_POINTS_TRANSACTION_HISTORY: {
    key: 'multilingual_points_transaction_history',
    label: 'Multilingual Points Transaction History',
    subtext:
      'Enables the Loyalty Transaction Labels section in Localisation settings. Required for multilanguage Points Transaction History.'
  },
  MULTIPLE_RAFFLE_TICKETS: {
    key: 'multiple_raffle_tickets',
    label: 'Issue multiple raffle tickets',
    subtext:
      'Enables configurable multiple raffle ticket issuance through raffle rules and the Customer module.'
  },
  MULTIPLE_REWARD_ISSUANCE: {
    key: 'multiple_reward_issuance',
    label: 'Multiple Reward Issue',
    subtext: 'Enables multiple reward issue feature in reward.'
  },
  NOTIFICATION_PREFERENCES: {
    key: 'notification_preferences',
    label: 'Notification Preferences',
    subtext: FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  NUMERIC_VOUCHER_CODE: {
    key: 'numeric_voucher_code_format',
    label: 'Voucher Code Format - Numeric',
    subtext: 'Enables numeric voucher code format option.'
  },
  OBJECT_LEVEL_PERMISSION: {
    key: 'object_level_permission',
    label: 'Object Level Permissions',
    subtext: 'Enables Access Groups settings for users.'
  },
  OPTIONAL_STAMP_POSITION_RULE_TRIGGER: {
    key: 'optional_stamp_position_rule_trigger',
    label: 'Optional stamp position rule trigger',
    subtext: 'Makes the Stamps Slot field optional on the Stamps Issued rule trigger.'
  },
  OUTCOME_POPUP_MESSAGE_SETTINGS: {
    key: 'outcome_popup_message',
    label: 'Outcome pop-up message settings for progress, quest and stamp campaigns',
    subtext: FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  OWNER_TAG: {
    key: 'object_owner',
    label: 'Owner Tag',
    subtext: 'Enables Owner Tag for Campaigns, Rules, Rewards, etc.'
  },
  PENDO_CONFIGURATION: {
    key: 'pendo_configuration',
    label: 'Pendo Configuration',
    subtext: 'Enables pendo configuration in portal'
  },
  PERX_3_REDESIGN: {
    key: 'perx_3_redesign',
    label: 'Perx 3 redesign (light Intelligence Suite + Orbit)',
    subtext: FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  PERX_AI_WORKSPACE: {
    key: 'perx_ai_workspace',
    label: 'Perx AI WorkSpace',
    subtext:
      'Master switch for Perx AI Workspace. Each module (Intelligence Suite, Perx Orbit and other AI Modules) has its own feature flag, but requires this master flag to be enabled.'
  },
  LAYER_6_DASHBOARD: {
    key: 'layer_6_dashboard',
    label: 'Perx Overwatch',
    subtext:
      'Enables Perx Overwatch to provide tenant-level business, operational, commercial, and support visibility with role-based access.'
  },
  PF_REPORT_GENERATION: {
    key: 'pf_report_generation',
    label: 'PF Report Generation',
    subtext: 'Enables Scheduled Reports for PF'
  },
  PLINKO_GAME: {
    key: 'plinko',
    label: 'Game Type - Plinko',
    subtext: 'Enables Game type - Plinko'
  },
  POS_TRIGGER_POST_CREATE_ACTIONS: {
    key: 'pos_trigger_post_create_actions',
    label: 'POS Trigger Create Actions',
    subtext:
      'Enrolls users to loyalty programs and creates sing up event when user uploaded via v4/pos/user_account enpoint'
  },
  PRICE_IDENTIFIER: {
    key: 'price_identifier',
    label: 'Price Identifier',
    subtext: 'Enables a custom identifier for reward selling price'
  },
  PRIVATE_REWARDS: {
    key: 'private_rewards',
    label: 'Private Rewards',
    subtext: 'Enables private reward type'
  },
  PRIZE_SETS: {
    key: 'prize_set_outcomes',
    label: 'Prize Sets as Outcome',
    subtext: FEATURE_SUBTEXT.QA
  },
  PROGRESS_CAMPAIGN: {
    key: 'progress_campaign',
    label: 'Campaign Type - Progress Campaign',
    subtext: 'Enables Campaign type - Progress Campaign'
  },
  PROGRESS_CAMPAIGN_CUSTOM_LEVEL_BADGES: {
    key: 'progress_campaign_custom_level_badges',
    label: 'Progress Campaign Custom Level Badges',
    subtext:
      'Enables custom image badges for Progress Campaign level states (In Progress, Not Started) in addition to Level Completed badge'
  },
  PROGRESS_RULE_TRIGGER: {
    key: 'progress_rule_trigger',
    label: 'Progress Rule Trigger',
    subtext: 'Enables a progress rule trigger option in campaign trigger of rule'
  },
  PROVISION_RATE: {
    key: 'provision_rate',
    label: 'Provision Rate',
    subtext:
      'Enables End of Month report to be generated with fixed provision rate value. Enabling this, Provision Rate Tier feature flag will be disabled.'
  },
  PROVISION_RATE_TIERING: {
    key: 'provision_rate_tiering',
    label: 'Provision Rate Tier',
    subtext:
      'Enables Provision Rate Tier feature for the End Of Month Report generation. Enabling this, Provision Rate feature flag will be disabled.'
  },
  PRIVATE_REWARD: {
    key: 'block_private_rewards',
    label: 'Block Private Reward',
    subtext: 'Enable to block private reward via reward issue api'
  },
  QUEST_CAMPAIGNS: {
    key: 'quest_campaigns',
    label: 'Campaign Type - Quest',
    subtext: 'Enables Campaign type - Quest'
  },
  QUICKSIGHT: {
    key: 'quicksight',
    label: 'QuickSight',
    subtext: FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  QUIZ_CAMPAIGNS: {
    key: 'quiz',
    label: 'Game Type - Quiz',
    subtext: 'Enables Game type - Quiz'
  },
  RAFFLE_CAMPAIGNS: {
    key: 'raffle_campaigns',
    label: 'Campaign Type - Raffle',
    subtext: 'Enables Campaign type - Raffle'
  },
  RAFFLE_TICKET_REVERSAL: {
    // TODO(PLA-9411, AF-9): key is "proposed" in the FR, unconfirmed against the backend.
    key: 'raffle_ticket_reversal',
    label: 'Raffle Ticket Reversal',
    subtext: 'Enables reversal of raffle tickets'
  },
  RANDOMIZE_REWARDS_PROBABILITY: {
    key: 'randomize_rewards_probability',
    label: 'Randomize Rewards Probability',
    subtext: 'Enables randomization of reward probabilities for campaign outcomes'
  },
  RECURRING_CAMPAIGNS: {
    key: 'recurring_campaigns',
    label: 'Recurring Campaigns',
    subtext: 'Enables settings for campaigns to run only on certain days and timings.'
  },
  RECURRING_REWARDS: {
    key: 'recurring_rewards',
    label: 'Recurring Rewards',
    subtext: 'Enables settings for rewards to be displayed only on certain days and timings.'
  },
  REDIRECT_TO_IDP_ON_LOGOUT: {
    key: 'redirect_to_idp_on_logout',
    label: 'Redirect to IdP on logout',
    subtext:
      'Sends SAML-authenticated users to the identity provider instead of the sign-in page when they log out or their session expires. Requires SAML to be configured for the tenant.'
  },
  REPORTS: {
    key: 'reports',
    label: 'Reports'
  },
  REWARD_ACTIVATION_DATE: {
    key: 'reward_activation_date',
    label: 'Reward Activation Date',
    subtext: 'Enables setting the activation date for a reward.'
  },
  REWARD_BUDGETING: {
    disabled: true,
    key: 'reward_budgeting',
    label: 'Reward Budgeting',
    subtext: 'Backend assistance required to enable this feature'
  },
  REWARD_CAMPAIGN: {
    key: 'reward_campaign',
    label: 'Campaign Type - Reward Campaign',
    subtext: 'Enables Campaign type - Reward Campaign'
  },
  REWARD_COST: {
    key: 'rewards_cost',
    label: 'Rewards Cost Input'
  },
  REWARD_LEGACY_AUDIENCE: {
    key: 'reward_legacy_audience',
    label: 'Rewards Legacy Inline Audience',
    subtext: 'Rewards Inline Audience - Where can this be displayed?'
  },
  REWARD_REDEMPTION_OUTCOMES: {
    key: 'reward_redemption_outcomes',
    label: 'Reward Redemption Outcomes',
    subtext: '"Loyalties" flag must also be enabled for this to take effect'
  },
  REWARD_SELLING_PRICE_CASH: {
    key: 'reward_selling_price_cash',
    label: 'Reward Selling Price Cash',
    subtext: 'Enables "cash" input in the selling price modal in rewards module.'
  },
  REWARD_TYPE_FILTER_TAB: {
    key: 'reward_type_filter_tab',
    label: 'Rewards Type Filter Tab',
    subtext: 'Enables the type filter - "Private" and "System" - tabs in Rewards list table.'
  },
  REWARDS: {
    key: 'rewards',
    label: 'BI - Rewards',
    subtext: 'Displays Rewards tab in Analytics module'
  },
  REWARDS_ARCHIVING: {
    key: 'rewards_archiving',
    label: 'Rewards Archiving',
    subtext: 'Rewards Archiving Allows End Users to move vouchers to archived.'
  },
  REWARDS_AUDIENCE: {
    key: 'reward_audience',
    label: 'Rewards Audience'
  },
  REWARDS_DISPLAY_EVEN_WHEN: {
    key: 'rewards_display_even_when',
    label: 'Rewards "Display even when limit is reached" settings'
  },
  REWARDS_FORM_CATALOG_ASSIGNMENT: {
    key: 'rewards_form_catalog_assignment',
    label: 'Rewards Form Catalog Direct Assignment',
    subtext: FEATURE_SUBTEXT.DEPRECATED
  },
  REWARDS_GIFTING: {
    key: 'rewards_gifting',
    label: 'Rewards Gifting',
    subtext: 'Rewards Gifting Allows End Users to Give Each Other Vouchers.'
  },
  REWARDS_LIST_STOCK_FILTERS: {
    key: 'rewards_list_stock_filters',
    label: 'Rewards List Stock Filters',
    subtext: 'Shows Selling Fast (<10% left) and Sold Out tabs in rewards list.'
  },
  REWARDS_MARKETPLACE: {
    key: 'rewards_marketplace',
    label: 'Rewards Marketplace',
    subtext: 'Enables Rewards Marketplace in the main sidebar navigation'
  },
  REWARDS_REDEMPTION_COUNTDOWN: {
    key: 'rewards_redemption_countdown',
    label: 'Rewards Redemption Offline Countdown',
    subtext: FEATURE_SUBTEXT.DEPRECATED
  },
  REWARD_REDEMPTION_LIMIT: {
    key: 'reward_total_redemption_limit',
    label: 'Enable Total Reward Redemption Limit',
    subtext: 'Enables reward redemption limit field in reward mechanics'
  },
  REWARDS_BULK_UPLOAD: {
    key: 'reward_upload',
    label: 'Rewards Bulk Upload',
    subtext: 'Enables bulk upload for rewards.'
  },
  REWARDS_TRANSACTION_ISSUED_TAB: {
    key: 'rewards_transaction_issued_tab',
    label: 'Rewards Legacy Transaction Issued Tab',
    subtext: FEATURE_SUBTEXT.DEPRECATED
  },
  REWARDS_VALIDITY_PERIOD_END_OF_MONTH: {
    key: 'rewards_validity_period_end_of_month',
    label: 'Rewards Mechanics Validity Period End of the Month'
  },
  RISK_DISCLAIMER: {
    key: 'risk_disclaimer',
    label: 'Risk Disclaimer Field',
    subtext: 'Enables risk disclaimer field in Campaign and Reward creation'
  },
  RULE_BALANCE_COL: {
    key: 'rule_balance_col',
    label: 'Rule Balance Column',
    subtext: 'Enables the balance column in rule list table.'
  },
  RULE_EDITING: {
    key: 'rule_editing',
    label: 'Allow editing of rules'
  },
  RULE_GROUP_CAMPAIGN: {
    key: 'rule_group_campaign',
    label: 'Campaign Type - Multiple Rules',
    subtext: 'Enables Campaign type - Multiple Rules'
  },
  RULE_TRIGGER_LIMIT_ON_CAMPAIGN_LEVEL: {
    key: 'rule_trigger_limit_on_campaign_level',
    label: 'Rule Trigger Limit on Campaign/Loyalty Level',
    subtext: 'Enables rule trigger limit (on campaign/loyalty level)'
  },
  RULES: {
    key: 'transaction_rules',
    label: 'Rules',
    subtext: 'Currently does not control anything'
  },
  SCHEDULED_TASK: {
    key: 'scheduled_tasks',
    label: 'Scheduled Tasks',
    subtext: 'Enables Scheduled Tasks module'
  },
  SCRATCH_CARD: {
    key: 'scratch_card',
    label: 'Game Type - Scratch Card',
    subtext: 'Enables Game type - Scratch Card'
  },
  SEND_REPORT_EMAIL: {
    key: 'send_report_email',
    label: 'Send Report via Email',
    subtext: 'Enables emailing a completed report as a CSV attachment from the Reports module'
  },
  SETTINGS: {
    key: 'settings',
    label: 'Settings',
    subtext: 'Currently does not control anything'
  },
  SHAKE_THE_TREE: {
    key: 'shake_the_tree',
    label: 'Game Type - Shake The Tree',
    subtext: 'Enables Game type - Shake The Tree'
  },
  SET_UP_CREDIT_POINT_LIMIT: {
    key: 'credit_point_limits',
    label: 'Set Up Credit Point Limit',
    subtext: 'Enable to set the credit point limit in the Loyalty Program'
  },
  SINGLE_LOYALTY: {
    key: 'single_loyalty_program',
    label: 'Single Loyalty',
    subtext: 'Allow only 1 Loyalty program to be set up'
  },
  SOCIAL_MEDIA_INTEGRATION: {
    key: 'social_media_integration',
    label: 'Social Media Integration',
    subtext: 'Enables Social Media settings for Campaigns, Catalogues, and Rewards'
  },
  SPIN_THE_WHEEL: {
    key: 'spin_the_wheel',
    label: 'Game Type - Spin the Wheel',
    subtext: 'Enables Game type - Spin the Wheel'
  },
  SPIN_THE_WHEEL_V2_ASSIGN_OUTCOMES_PER_WEDGE: {
    key: 'spin_the_wheel_v2_assign_outcomes_per_wedge',
    label: 'Spin the Wheel 2.0 - Assign Outcomes per Wedge',
    subtext:
      'Enable Spin the Wheel 2.0. Replaces the legacy spin the wheel interface in the dashboard with the new one that allows setting of fixed outcomes per wedge'
  },
  SSO_LOGIN_SUPPORT: {
    key: 'sso_login_support',
    label: 'SSO Login Support',
    subtext: 'Enables SSO login support'
  },
  STAMP: {
    key: 'stamp_campaign',
    label: 'Campaign Type - Stamp',
    subtext: 'Enables Campaign type - Stamp'
  },
  STASH_VOUCHER_CODE_SETUP: {
    key: 'Stash_voucher_code_setup',
    label: 'Stash Voucher Code Setup',
    subtext: 'Enables stash voucher code setup in reward'
  },
  STREAK_CAMPAIGNS: {
    key: 'streak_campaign',
    label: 'Campaign Type - Streak',
    subtext: 'Enables streak campaigns'
  },
  SURVEY: {
    key: 'survey',
    label: 'Game Type - Survey',
    subtext: 'Enables Game type - Survey'
  },
  SURVEY_CAMPAIGN_AUDIENCES: {
    key: 'survey_campaign_audiences',
    label: 'Survey Campaign Audiences',
    subtext: "Create Audiences based on survey campaign's answers"
  },
  TEAMS: {
    key: 'teams',
    label: 'Team-based campaigns',
    subtext: 'Enables teams in stamp and progress campaigns. ' + FEATURE_SUBTEXT.QA
  },
  TIER_BASED_REFERRAL_REWARD: {
    key: 'tier_based_referral_reward',
    label: 'Tier-Based Referral Reward',
    subtext: 'Enables tier-based referral reward configuration in Referral Campaigns.'
  },
  TIME_SALE_FOR_REWARDS_SELLING_PRICE: {
    key: 'total_sale_for_reward_selling_price',
    label: 'Time Sale for Rewards Selling Price',
    subtext: 'Time Sale for Rewards Selling Price'
  },
  TRANSACTION_REVERSAL: {
    key: 'transaction_reversal',
    label: 'Transaction Reversal',
    subtext: 'Enables transaction reversal feature'
  },
  TRANSACTION_RULE_TRIGGER: {
    key: 'transaction_rule_trigger',
    label: 'Transaction Rule Trigger',
    subtext: 'Enables a transaction rule trigger option in rule'
  },
  TRANSACTION_SETTLEMENT_DATE_FILTER: {
    key: 'transaction_settlement_date_filter',
    label: 'Transaction Settlement Date Filter',
    subtext:
      'Enables transaction settlement date filter in Reports while generating Loyalty Breakdown Reports'
  },
  UPLOAD_ACTIONS_PROCESSED_ROWS: {
    key: 'upload_actions_processed_rows',
    label: 'Upload Actions - Processed Rows',
    subtext: 'Shows processed rows column in upload tabs setting list.'
  },
  VOUCHER_CODE_MODS: {
    key: 'generate_system_voucher_codes',
    label: 'Voucher Code Modifications',
    subtext: 'Changes the limits of System Generated rewards. ' + FEATURE_SUBTEXT.IN_DEVELOPMENT
  },
  VOUCHER_CODE_PREFIX: {
    key: 'voucher_code_prefix',
    label: 'Voucher Code Prefix',
    subtext: 'Enables system generated voucher code format prefix'
  },
  MANUAL_CLAIM_RAFFLE_TICKET: {
    key: 'manual_claim_raffle_ticket',
    label: 'Raffle Campaigns - Tickets must be claimed manually',
    subtext: 'Enables manual claim raffle ticket'
  },
  NO_OUTCOME_IN_GAME_CAMPAIGN: {
    key: 'no_outcome_in_game_campaign',
    label: 'No outcome button in games',
    subtext: 'No outcome button in games'
  },
  SKIP_TRANSACTION_FROM_UPLOAD_TRANSACTION_CSV: {
    key: 'skip_transaction_from_upload_transaction_csv',
    label: 'Skip Transaction from Upload Transaction CSV',
    subtext: 'Enables Skip Transaction from Upload Transaction CSV'
  },
  Process_Transaction_From_Upload_Transaction_CSV: {
    key: 'process_transaction_from_upload_transaction_csv',
    label: 'Process Transaction from Upload Transaction CSV',
    subtext: 'Enables Process Transaction from Upload Transaction CSV'
  },
  RESET_POINTS_ON_DEACTIVATE_CUSTOMER: {
    key: 'reset_points_on_deactivate_customer',
    label: 'Reset Points On Deactivate Customer',
    subtext: 'Enables reset earn points on customer deactivated'
  },
  COMMON_IMAGES_FOR_ALL_LANGUAGES: {
    key: 'common_images_for_all_languages',
    label: 'Common Images for all languages in Campaigns, Rewards, Badge, Loyalty & Merchant',
    subtext: 'When enabled, any images uploaded in any module is common for all languages'
  }
} as const);

export type FeatureFlagKey = (typeof FEATURES)[keyof typeof FEATURES]['key'];

/**
 * Define mutually exclusive feature flag pairs
 * When enabling a feature in a pair, the other feature will be auto-disabled by backend
 * Add new pairs here to enable mutual exclusivity for other features
 */
export const MUTUALLY_EXCLUSIVE_FEATURES: Record<string, Feature> = {
  [FEATURES.USER_ROLE_GRANULAR_PERMISSIONS_CUSTOMER_MODULE.key]:
    FEATURES.MAKER_CHECKER_CUSTOMER_MANAGEMENT,
  [FEATURES.MAKER_CHECKER_CUSTOMER_MANAGEMENT.key]:
    FEATURES.USER_ROLE_GRANULAR_PERMISSIONS_CUSTOMER_MODULE
};

// Used for image upload validation
export const SUPPORTED_IMAGE_FORMATS = '.jpg, .jpeg, .png, .svg';

//Maker Checker Constants
export const APPROVE_REQUEST_SUCCESS_MESSAGE = 'Request has been approved';
export const MULTIPLE_APPROVE_REQUEST_SUCCESS_MESSAGE = 'Requests have been approved';

export const REQUEST_TYPE = {
  PUBLISHED: 'publish_draft',
  UPDATE: 'update',
  DEACTIVATE: 'deactivate',
  REACTIVATE: 'reactivate'
};

export const RULE_DETAILS_ATTRIBUTES = ['name', 'labels', 'notes'];

export const RULE_TRIGGER_ATTRIBUTES = [
  'rule_type',
  'event_name',
  'event_params',
  'trigger_limit',
  'account_limit',
  'conditions',
  'account_interval_limit',
  'account_interval_units',
  'account_interval_period',
  'rule_trigger_interval_max_available',
  'rule_trigger_interval_units',
  'rule_trigger_interval_period',
  'begins_at',
  'ends_at',
  'trigger_available'
];

export const RULE_OUTCOME_ATTRIBUTES = [
  'outcome_id',
  'outcome_type',
  'outcome_value',
  'outcome_expires_at',
  'outcome_options',
  'send_notification',
  'include_outcome_audience_ids',
  'exclude_outcome_audience_ids',
  'outcome_base',
  'action_name',
  'earn_type',
  'max_outcome_value',
  'outcomes'
];

export const MAX_OUTCOMES = 10;

export const ERROR_CODES = {
  41214: ERRORS.APPROVE_OWN_REQUEST
};

export const ERROR_MESSAGES = {
  "Event 'expire' cannot transition from 'expired'.": 'Reward is expired and cannot be approved.'
};

export const AUDIENCE_WHITELISTED_ATTRIBUTES = [
  'filters',
  'audience_ids',
  'exclude_audience_ids',
  'include_audience_ids'
] as const;

export const WHITELISTED_SINGTEL_TENANTS_IDS = [879, 67, 1316, 1315];
